import React from 'react';
import {
  Home,
  Users,
  CalendarDays,
  ShoppingCart,
  Utensils,
  BarChart3,
  Settings,
  Bell,
  Search,
  Plus,
  Check,
  Trash2,
  Pencil,
  Eye,
  TrendingUp,
} from 'lucide-react';

import {
  CampButton,
  CampCard,
  CampBadge,
  CampInput,
} from '../components/camp-ui';

const menu = [
  [Home, 'Inicio'],
  [Users, 'Clientes'],
  [CalendarDays, 'Reservas'],
  [ShoppingCart, 'Ventas'],
  [Utensils, 'Cantina'],
  [BarChart3, 'Reportes'],
  [Settings, 'Configuración'],
];

const GradientButton = ({ children, className = '', ...props }) => (
  <button
    className={[
      'inline-flex min-h-10 items-center justify-center gap-2',
      'rounded-xl px-4 text-sm font-semibold text-white',
      'bg-gradient-to-r from-azul-oscuro via-azul-lago to-verde-esmeralda',
      'shadow-sm transition hover:brightness-105 hover:shadow-md',
      'focus:outline-none focus:ring-4 focus:ring-verde-esmeralda/20',
      className,
    ].join(' ')}
    {...props}
  >
    {children}
  </button>
);

const Kpi = ({ title, value, detail, Icon, iconClass }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </p>
        <p className="mt-2 text-2xl font-bold text-slate-900">
          {value}
        </p>
      </div>

      <div className={`flex h-11 w-11 items-center justify-center rounded-full ${iconClass}`}>
        <Icon size={21} />
      </div>
    </div>

    <div className="mt-3 flex items-center gap-1 text-xs font-medium text-verde-oscuro">
      <TrendingUp size={14} />
      {detail}
    </div>
  </div>
);

export default function CampUIKit() {
  return (
    <div className="min-h-screen bg-slate-50 font-camp text-slate-900">

      {/* NAVBAR */}
      <header className="sticky top-0 z-30 px-4 pt-4">
        <div className="
          mx-auto flex max-w-[1500px] items-center justify-between
          rounded-2xl px-5 py-3 text-white shadow-lg
          bg-gradient-to-r
          from-azul-oscuro
          via-[#087C78]
          to-verde-esmeralda
        ">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white px-4 py-2 text-left shadow-sm">
              <div className="text-xl font-black tracking-[0.12em] text-verde-oscuro">
                CAMP
              </div>
              <div className="text-[8px] font-bold tracking-[0.22em] text-black">
                BY DRIZA
              </div>
            </div>

            <div className="hidden items-center gap-1 lg:flex">
              {['Inicio', 'Clientes', 'Reservas', 'Ventas', 'Cantina'].map((item) => (
                <button
                  key={item}
                  className={`
                    rounded-lg px-3 py-2 text-sm font-medium transition
                    ${item === 'Inicio'
                      ? 'bg-white/15'
                      : 'hover:bg-white/10'}
                  `}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button className="rounded-full p-2 transition hover:bg-white/10">
              <Bell size={20} />
            </button>

            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold">Administrador</div>
              <div className="text-xs text-white/70">Online</div>
            </div>

            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-azul-oscuro">
              <Users size={20} />
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1500px] gap-5 px-4 py-5 lg:grid-cols-[220px_1fr]">

        {/* SIDEBAR */}
        <aside className="
          hidden min-h-[760px] overflow-hidden rounded-2xl text-white shadow-lg
          bg-gradient-to-b
          from-azul-oscuro
          via-[#096E79]
          to-[#07586B]
          lg:flex lg:flex-col
        ">
          <div className="p-3">
            {menu.map(([Icon, label], index) => (
              <button
                key={label}
                className={[
                  'mb-1 flex w-full items-center gap-3 rounded-xl px-3 py-3',
                  'text-left text-sm font-medium transition',
                  index === 0
                    ? 'bg-gradient-to-r from-azul-lago to-verde-esmeralda shadow-sm'
                    : 'hover:bg-white/10',
                ].join(' ')}
              >
                <Icon size={19} />
                {label}
              </button>
            ))}
          </div>

          <div className="mt-auto border-t border-white/10 p-5">
            <div className="text-xl font-black tracking-[0.14em]">
              CAMP
            </div>
            <div className="mt-1 text-[9px] font-semibold tracking-[0.25em] text-white/70">
              BY DRIZA
            </div>
          </div>
        </aside>

        <main className="space-y-5">

          {/* KPI */}
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi
              title="Clientes activos"
              value="1.248"
              detail="+12% este mes"
              Icon={Users}
              iconClass="bg-emerald-100 text-emerald-700"
            />

            <Kpi
              title="Reservas hoy"
              value="28"
              detail="+8% vs ayer"
              Icon={CalendarDays}
              iconClass="bg-blue-100 text-blue-700"
            />

            <Kpi
              title="Ventas del día"
              value="$ 256.430"
              detail="+15%"
              Icon={ShoppingCart}
              iconClass="bg-green-100 text-green-700"
            />

            <Kpi
              title="Ingresos del mes"
              value="$ 5.430.000"
              detail="+10%"
              Icon={BarChart3}
              iconClass="bg-amber-100 text-amber-700"
            />
          </section>

          {/* HERO */}
          <section className="
            relative overflow-hidden rounded-3xl
            bg-gradient-to-br
            from-azul-oscuro
            via-azul-lago
            to-verde-esmeralda
            px-8 py-10 text-white shadow-lg
          ">
            <div className="
              absolute -right-20 -top-20
              h-64 w-64 rounded-full
              bg-white/10 blur-2xl
            " />

            <div className="
              absolute -bottom-24 right-24
              h-56 w-56 rounded-full
              bg-verde-esmeralda/30 blur-3xl
            " />

            <div className="relative max-w-xl text-left">
              <div className="mb-3 text-sm font-medium text-white/80">
                Bienvenido a
              </div>

              <h1 className="text-4xl font-black tracking-tight sm:text-5xl">
                Driza Camp
              </h1>

              <p className="mt-4 max-w-md text-sm leading-6 text-white/85">
                Gestión de tu camping de forma simple, rápida y eficiente.
              </p>
            </div>
          </section>

          {/* COMPONENTES */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-6 text-left">
              <h2 className="text-xl font-bold text-azul-oscuro">
                Componentes Camp
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Identidad visual para toda la aplicación.
              </p>
            </div>

            <div className="grid gap-8 xl:grid-cols-4">

              {/* botones */}
              <div className="space-y-3 text-left">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Botones
                </h3>

                <GradientButton className="w-full">
                  <Check size={17} />
                  Botón principal
                </GradientButton>

                <CampButton variant="outline" className="w-full">
                  Botón secundario
                </CampButton>

                <CampButton variant="danger" className="w-full">
                  <Trash2 size={16} />
                  Eliminar
                </CampButton>
              </div>

              {/* estados */}
              <div className="text-left">
                <h3 className="mb-4 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Estados
                </h3>

                <div className="flex flex-wrap gap-2">
                  <CampBadge variant="success">Activo</CampBadge>
                  <CampBadge variant="warning">Pendiente</CampBadge>
                  <CampBadge variant="danger">Cancelado</CampBadge>
                  <CampBadge variant="info">Información</CampBadge>
                </div>
              </div>

              {/* inputs */}
              <div className="space-y-3 text-left">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Inputs
                </h3>

                <CampInput placeholder="Campo de texto" />

                <div className="relative">
                  <Search
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    placeholder="Buscar..."
                    className="
                      w-full rounded-xl border border-slate-200
                      py-2.5 pl-10 pr-3 text-sm
                      outline-none transition
                      focus:border-verde-esmeralda
                      focus:ring-4 focus:ring-verde-esmeralda/10
                    "
                  />
                </div>
              </div>

              {/* card */}
              <div className="text-left">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Card
                </h3>

                <CampCard className="!rounded-2xl">
                  <div className="font-bold text-azul-oscuro">
                    Título de la card
                  </div>

                  <p className="mt-2 text-sm text-slate-500">
                    Elemento limpio con acción destacada.
                  </p>

                  <GradientButton className="mt-5 w-full">
                    <Plus size={16} />
                    Acción
                  </GradientButton>
                </CampCard>
              </div>

            </div>
          </section>

          {/* TABLA */}
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="
              px-6 py-4 text-left text-white
              bg-gradient-to-r from-azul-oscuro to-verde-esmeralda
            ">
              <h2 className="font-bold">
                Ejemplo de tabla
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-5 py-3">ID</th>
                    <th className="px-5 py-3">Cliente</th>
                    <th className="px-5 py-3">Check In</th>
                    <th className="px-5 py-3">Check Out</th>
                    <th className="px-5 py-3">Estado</th>
                    <th className="px-5 py-3 text-right">Acciones</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {[
                    ['#1254', 'Juan Pérez', '10/05/2026', '12/05/2026', 'Activo'],
                    ['#1253', 'María Gómez', '09/05/2026', '11/05/2026', 'Pendiente'],
                    ['#1252', 'Carlos Rodríguez', '08/05/2026', '10/05/2026', 'Finalizado'],
                  ].map((row) => (
                    <tr key={row[0]} className="hover:bg-slate-50">
                      <td className="px-5 py-4 font-medium">{row[0]}</td>
                      <td className="px-5 py-4">{row[1]}</td>
                      <td className="px-5 py-4">{row[2]}</td>
                      <td className="px-5 py-4">{row[3]}</td>
                      <td className="px-5 py-4">
                        <CampBadge
                          variant={
                            row[4] === 'Activo'
                              ? 'success'
                              : row[4] === 'Pendiente'
                              ? 'warning'
                              : 'neutral'
                          }
                        >
                          {row[4]}
                        </CampBadge>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button className="text-azul-lago hover:text-azul-oscuro">
                            <Eye size={17} />
                          </button>
                          <button className="text-verde-oscuro hover:text-verde-esmeralda">
                            <Pencil size={17} />
                          </button>
                          <button className="text-red-500 hover:text-red-700">
                            <Trash2 size={17} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}
